import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Input from "../../../Utility/Input/Input";
import Checkbox from "../../../Utility/Checkbox/Checkbox";
import Button from "../../../Utility/Button/Button";
import FormSubmitButton from "../../../Utility/Button/FormSubmitButton";
import Typography from "../../../Utility/Typography";
import Dropdown from "../../../Utility/Dropdown/Dropdown";
import { LogTimeSchema } from "../../../FormsValidations/Validations";
import SingleDatePicker from "../../../Utility/DatePickerRange/SingleDatePicker";
import {
  addWorkItemTimeLog,
  fetchProjectsData,
} from "../../../Redux/Thunks/projectManagementThunk";
import { fetchAllWorkItemsForLogPicker } from "../../../Redux/Thunks/timeEntryThunk";
import { updateMyTimeLog } from "../../../Redux/Thunks/timeEntryThunk";
import toast from "react-hot-toast";
import { formatDateForInput, DISPLAY_DATE_FORMAT_PICKER_MOMENT } from "../../../utils/dateUtils";
import {
  parseJiraLogDurationToMinutes,
  parseBillableDurationToMinutes,
  minutesToJiraHoursDisplay,
  minutesToPrefillLogDuration,
} from "../../../utils/jiraLogDuration";
import { usePermissions } from "../../../hooks/usePermissions";
import { APP_ROLE } from "../../../permissions/roleKinds";
import { getApiErrorMessage, isSilentRequestAbortError } from "../../../utils/apiErrorMessage";
import {
  RiFileListLine,
  RiTimeLine,
  RiMenuLine,
  RiErrorWarningLine,
  RiCloseLine,
  RiFocus3Line,
} from "react-icons/ri";
import {
  selectLogPickerWorkItemsList,
  selectLogPickerWorkItemsLoading,
} from "../../../Redux/Slices/timeEntrySlice";
import { selectProjectsListForDropdown } from "../../../Redux/Slices/projectsManagementSlice";
import { getListFromApiResponse } from "../../utils/apiListMappers";
import {
  workItemToLogTimePickerLabel,
  workItemToLogTimePickerOption,
  buildMyTimeLogPatchBody,
} from "../../TimeEntry/timeEntryMappers";

/** First paint for edit mode — avoids empty calendar before effects run. */
function parseInitialLogDateForModal(mode, initialLogDate) {
  if (mode !== "edit" || initialLogDate == null) return null;
  const raw = String(initialLogDate).trim();
  if (!raw) return null;
  const ymd = raw.slice(0, 10);
  const d = new Date(`${ymd}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

const LogTimeModal = ({
  isOpen,
  onClose,
  issue,
  projectId,
  workItemId,
  onSuccess,
  mode = "create", // "create" | "edit"
  timeLogId = null,
  initialMinutesLogged = null,
  initialLogDate = null,
  initialComment = "",
  initialWorkItemKey = "",
  initialWorkItemTitle = "",
  /** Billable minutes when editing (admin); defaults to logged time when omitted. */
  initialBillableMinutes = null,
  /** Query params for scoped work-item lists: Time Entry passes client/project/team filters; Log Time applies them on GET /work-items/all (unpaginated all work items). */
  workItemPickerQueryParams = {},
}) => {
  const dispatch = useDispatch();
  const { roleKind } = usePermissions();
  const isAdmin = roleKind === APP_ROLE.ADMIN;
  const showBillableHours = isAdmin && mode === "edit";
  const pickerList = useSelector(selectLogPickerWorkItemsList);
  const pickerLoading = useSelector(selectLogPickerWorkItemsLoading);
  const projectsData = useSelector(selectProjectsListForDropdown);

  const [selectedWorkItemId, setSelectedWorkItemId] = useState(workItemId ?? null);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId ?? null);
  const [selectedIssue, setSelectedIssue] = useState(issue ?? null);

  const [date, setDate] = useState(() => parseInitialLogDateForModal(mode, initialLogDate));
  const [duration, setDuration] = useState("0h");
  const [billableDuration, setBillableDuration] = useState("0h");
  const [description, setDescription] = useState("");
  const [logAnother, setLogAnother] = useState(false);
  const [errors, setErrors] = useState({
    date: "",
    duration: "",
    billableDuration: "",
  });
  /** When false, billable field tracks logged duration until the admin edits billable manually. */
  const billableTouchedRef = useRef(false);
  const [useStartEndTime, setUseStartEndTime] = useState(false);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  /** Only sync props → local task/project when the modal opens, not on every parent re-render (e.g. after log + refetch). */
  const wasModalOpenRef = useRef(false);

  // Calculate duration from start and end time
  const calculateDuration = (start, end) => {
    if (!start || !end) return "0h";
    const [startH, startM] = start.split(":").map(Number);
    const [endH, endM] = end.split(":").map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    const diffMinutes = endMinutes - startMinutes;
    if (diffMinutes <= 0) return "0h";
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  useEffect(() => {
    if (useStartEndTime && startTime && endTime) {
      const calculated = calculateDuration(startTime, endTime);
      setDuration(calculated);
      if (showBillableHours && !billableTouchedRef.current) {
        setBillableDuration(calculated);
      }
    }
  }, [startTime, endTime, useStartEndTime, showBillableHours]);

  const handleDateChange = (payload) => {
    if (!payload?.date) {
      setDate(null);
      setErrors((prev) => ({ ...prev, date: "" }));
      return;
    }
    const d = new Date(`${payload.date}T12:00:00`);
    if (Number.isNaN(d.getTime())) {
      setDate(null);
      return;
    }
    setDate(d);
    setErrors((prev) => ({ ...prev, date: "" }));
  };

  const handleDurationChange = (e) => {
    const value = e.target.value;
    setDuration(value);
    if (value && value !== "0h") {
      setErrors((prev) => ({ ...prev, duration: "", billableDuration: "" }));
    }
    if (showBillableHours && !billableTouchedRef.current) {
      setBillableDuration(value);
    }
  };

  const handleBillableDurationChange = (e) => {
    billableTouchedRef.current = true;
    const value = e.target.value;
    setBillableDuration(value);
    if (value && value !== "0h") {
      setErrors((prev) => ({ ...prev, billableDuration: "" }));
    }
  };

  const handleBlur = async () => {
    try {
      await LogTimeSchema.validateAt("duration", { duration });
      setErrors((prev) => ({ ...prev, duration: "" }));
    } catch (error) {
      setErrors((prev) => ({ ...prev, duration: error.message }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    try {
      const formData = {
        date,
        duration,
        description,
        issue: selectedIssue,
        logAnother,
      };
      await LogTimeSchema.validate(formData, { abortEarly: false });
      setErrors({ date: "", duration: "", billableDuration: "" });

      const parsedDuration = parseJiraLogDurationToMinutes(duration);
      if (!parsedDuration.ok || parsedDuration.minutes <= 0) {
        setErrors((prev) => ({
          ...prev,
          duration: parsedDuration.error || "Enter a valid duration (e.g. 2.5h, 15m, 1h 30m).",
        }));
        return;
      }
      const minutesLogged = parsedDuration.minutes;

      let billableMinutesPayload;
      if (showBillableHours) {
        const billableInput =
          String(billableDuration ?? "").trim() === "" ? duration : billableDuration;
        const parsedBillable = parseBillableDurationToMinutes(billableInput, minutesLogged);
        if (!parsedBillable.ok) {
          setErrors((prev) => ({
            ...prev,
            billableDuration:
              parsedBillable.error || "Enter valid billable time (cannot exceed logged time).",
          }));
          return;
        }
        billableMinutesPayload = parsedBillable.minutes;
      }
      if (!selectedWorkItemId) {
        setSubmitError("Cannot log time: select an issue first.");
        return;
      }
      if (!selectedProjectId) {
        setSubmitError(
          "Cannot log time: the selected work item has no project context. Try again or contact support."
        );
        return;
      }
      const logDate = date ? formatDateForInput(date) || null : null;
      if (!logDate) {
        setErrors((prev) => ({ ...prev, date: "Date is required" }));
        return;
      }

      setSubmitting(true);
      if (mode === "edit") {
        if (!timeLogId) {
          setSubmitError("Cannot update: time log id not found.");
          return;
        }
        const patchBody = buildMyTimeLogPatchBody({
          isAdmin: showBillableHours,
          minutesLogged,
          logDate,
          comment: description.trim() || null,
          billableMinutes: showBillableHours ? billableMinutesPayload : undefined,
          initialMinutesLogged,
          initialBillableMinutes,
          initialLogDate,
          initialComment,
        });
        if (Object.keys(patchBody).length === 0) {
          setSubmitError("No changes to save.");
          setSubmitting(false);
          return;
        }
        await dispatch(
          updateMyTimeLog({
            id: timeLogId,
            ...patchBody,
          })
        ).unwrap();
        toast.success("Time log updated successfully.");
      } else {
        const comment = description.trim() || null;
        await dispatch(
          addWorkItemTimeLog({
            projectId: selectedProjectId,
            workItemId: selectedWorkItemId,
            minutesLogged,
            logDate,
            comment,
          })
        ).unwrap();
        toast.success("Time logged successfully.");
      }
      onSuccess?.();

      if (mode === "edit" || !logAnother) {
        handleClose();
      } else {
        setDate(new Date());
        setDuration("0h");
        setBillableDuration("0h");
        billableTouchedRef.current = false;
        setDescription("");
        setErrors({ date: "", duration: "", billableDuration: "" });
        setUseStartEndTime(false);
        setStartTime("");
        setEndTime("");
      }
    } catch (error) {
      if (error?.inner && Array.isArray(error.inner)) {
        const newErrors = { date: "", duration: "", billableDuration: "" };
        error.inner.forEach((err) => {
          if (err.path === "date") newErrors.date = err.message;
          else if (err.path === "duration") newErrors.duration = err.message;
        });
        setErrors(newErrors);
      } else if (error?.path === "date" || error?.path === "duration") {
        setErrors({
          date: error.path === "date" ? error.message : "",
          duration: error.path === "duration" ? error.message : "",
        });
      } else if (!isSilentRequestAbortError(error)) {
        setSubmitError(getApiErrorMessage(error, "Failed to save time log."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setDate(new Date());
    setDuration("0h");
    setBillableDuration("0h");
    billableTouchedRef.current = false;
    setDescription("");
    setLogAnother(false);
    setErrors({ date: "", duration: "", billableDuration: "" });
    setSubmitError("");
    setUseStartEndTime(false);
    setStartTime("");
    setEndTime("");
    onClose();
  };

  // Handle keyboard shortcut (Cmd/Ctrl + Enter)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && isOpen) {
        handleSubmit(e);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, date, duration, description]);

  const issuesList = useMemo(() => {
    const projects = getListFromApiResponse(projectsData);
    const resolveProjectId = (item) => {
      const raw = item?.projectId;
      if (raw != null && String(raw).trim() !== "") return String(raw).trim();
      const pn = String(item?.projectName ?? "").trim();
      if (!pn) return undefined;
      const hit = projects.find((p) => String(p.projectName ?? p.name ?? "").trim() === pn);
      return hit?.id != null ? String(hit.id).trim() : undefined;
    };

    const list = (Array.isArray(pickerList) ? pickerList : []).map((item) => ({
      ...item,
      projectId: item.projectId ?? resolveProjectId(item),
    }));

    if (!selectedIssue) return list;
    const selectedId = selectedIssue?.id;
    if (selectedId == null) return list;
    const exists = list.some((i) => String(i.id) === String(selectedId));
    if (exists) return list;
    const merged = {
      ...selectedIssue,
      projectId: selectedIssue.projectId ?? resolveProjectId(selectedIssue),
    };
    return [merged, ...list];
  }, [pickerList, selectedIssue, projectsData]);

  const issueOptions = useMemo(
    () => issuesList.map((item) => workItemToLogTimePickerOption(item)),
    [issuesList]
  );

  const jiraHoursPreview = useMemo(() => {
    const r = parseJiraLogDurationToMinutes(duration);
    if (!r.ok || r.minutes <= 0) return null;
    return minutesToJiraHoursDisplay(r.minutes);
  }, [duration]);

  const billableHoursPreview = useMemo(() => {
    if (!showBillableHours) return null;
    const logged = parseJiraLogDurationToMinutes(duration);
    if (!logged.ok) return null;
    const r = parseBillableDurationToMinutes(billableDuration, logged.minutes);
    if (!r.ok || r.minutes <= 0) return null;
    return minutesToJiraHoursDisplay(r.minutes);
  }, [showBillableHours, duration, billableDuration]);

  const editWorkItemLabel = useMemo(() => {
    if (selectedIssue) {
      return workItemToLogTimePickerLabel(selectedIssue) || String(selectedIssue.id ?? "—");
    }
    const k = initialWorkItemKey || "";
    const t = initialWorkItemTitle || "";
    return [k, t].filter(Boolean).join(" · ") || "—";
  }, [selectedIssue, initialWorkItemKey, initialWorkItemTitle]);

  /** When projects load after the picker, attach resolved `projectId` for submit. */
  useEffect(() => {
    if (!selectedWorkItemId) return;
    const found = issuesList.find((i) => String(i.id) === String(selectedWorkItemId));
    if (found?.projectId) setSelectedProjectId(String(found.projectId));
  }, [issuesList, selectedWorkItemId]);

  // Initialize selection + (if needed) fetch all work items for picker — only when opening, so "Log another" keeps the same task.
  useEffect(() => {
    if (!isOpen) {
      wasModalOpenRef.current = false;
      return;
    }

    const justOpened = !wasModalOpenRef.current;
    wasModalOpenRef.current = true;
    if (!justOpened) return;

    setSelectedWorkItemId(workItemId ?? null);
    setSelectedProjectId(projectId ?? null);
    setSelectedIssue(issue ?? null);

    if (mode !== "edit") {
      setDate(new Date());
      setDuration("0h");
      setBillableDuration("0h");
      billableTouchedRef.current = false;
      setDescription("");
      setErrors({ date: "", duration: "", billableDuration: "" });
      setSubmitError("");
    }

    const shouldFetchIssues = workItemId == null || projectId == null || issue == null;
    if (shouldFetchIssues) {
      dispatch(fetchAllWorkItemsForLogPicker(workItemPickerQueryParams ?? {}));
    }
    if (getListFromApiResponse(projectsData).length === 0) {
      dispatch(fetchProjectsData({ getAll: true }));
    }
  }, [
    dispatch,
    isOpen,
    issue,
    mode,
    projectId,
    workItemId,
    workItemPickerQueryParams,
    projectsData,
  ]);

  // Prefill form when editing an existing time log
  useEffect(() => {
    if (!isOpen) return;
    if (mode !== "edit") return;

    if (initialLogDate) {
      const d = new Date(`${String(initialLogDate).slice(0, 10)}T12:00:00`);
      if (!Number.isNaN(d.getTime())) setDate(d);
    } else {
      setDate(new Date());
    }
    if (initialMinutesLogged != null) {
      const loggedPrefill = minutesToPrefillLogDuration(initialMinutesLogged);
      setDuration(loggedPrefill);
      const billableMins =
        initialBillableMinutes != null ? Number(initialBillableMinutes) : initialMinutesLogged;
      setBillableDuration(minutesToPrefillLogDuration(billableMins));
      billableTouchedRef.current =
        initialBillableMinutes != null &&
        Number(initialBillableMinutes) !== Number(initialMinutesLogged);
    }
    setDescription(initialComment ?? "");
    setLogAnother(false);
  }, [isOpen, mode, initialMinutesLogged, initialBillableMinutes, initialLogDate, initialComment]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        className="bg-surface-primary rounded-lg shadow-lg w-full max-w-lg relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Custom Header with Green Checkmark */}
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <div className="flex items-center gap-3">
            <Typography variant="h5" className="font-semibold">
              Log Time
            </Typography>
          </div>

          <Button
            type="button"
            onClick={handleClose}
            className="!border-0 !bg-transparent !p-0 !shadow-none text-text-secondary hover:text-text-primary"
            aria-label="Close modal"
          >
            <RiCloseLine className="w-5 h-5" />
          </Button>
        </div>

        {/* Body */}
        <div className="p-4">
          <form onSubmit={handleSubmit}>
            {/* Task/Issue Selection */}
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <RiFileListLine className="text-text-secondary w-5 h-5 flex-shrink-0" />
                <div className="relative flex-1">
                  {mode === "edit" ? (
                    <Input readOnly value={editWorkItemLabel} className="h-10" noWrapper />
                  ) : (
                    <Dropdown
                      name="issue"
                      value={selectedWorkItemId ?? ""}
                      options={issueOptions}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        setSelectedWorkItemId(selectedId);
                        const found = issuesList.find((i) => String(i.id) === String(selectedId));
                        setSelectedIssue(found ?? null);
                        setSelectedProjectId(found?.projectId ?? null);
                      }}
                      placeholder={pickerLoading ? "Loading work items..." : "Select issue..."}
                      searchable
                      disabled={pickerLoading && issueOptions.length === 0}
                      className="text-sm"
                      noWrapper
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Date and Duration Row */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Date Field */}

              <div>
                <Typography className="mb-1 block font-medium text-text-primary">Date</Typography>

                <div className="flex items-start gap-2">
                  {/* Icon column */}
                  <div className="pt-2">
                    <RiTimeLine className="text-text-secondary w-5 h-5" />
                  </div>

                  {/* Input + error column */}
                  <div className="flex-1">
                    <SingleDatePicker
                      key={date ? formatDateForInput(date) : "no-date"}
                      value={date ? { date: formatDateForInput(date) } : null}
                      onChange={handleDateChange}
                      placeholder={DISPLAY_DATE_FORMAT_PICKER_MOMENT}
                      className={errors.date ? "!border-red-500" : ""}
                    />

                    {errors.date && (
                      <Typography className="text-red-500 text-xs mt-1">{errors.date}</Typography>
                    )}
                  </div>
                </div>
              </div>

              {/* Duration Field */}
              <div>
                <Typography className="mb-1 block font-medium text-text-primary">
                  Duration
                </Typography>
                <div className="relative">
                  <Input
                    value={duration}
                    onChange={handleDurationChange}
                    onBlur={handleBlur}
                    placeholder="e.g. 2.5h, .25h, 15m, 1h 30m"
                    className={`h-10 ${errors.duration ? "border-red-500 pr-10" : ""}`}
                    noWrapper
                  />
                  {errors.duration && (
                    <RiErrorWarningLine className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500 w-5 h-5" />
                  )}
                </div>
                {jiraHoursPreview && (
                  <Typography variant="text2" className="text-text-secondary text-xs mt-1">
                    ≈ {jiraHoursPreview} <span className="sr-only">hours (Jira-style)</span>
                  </Typography>
                )}
                {errors.duration && (
                  <Typography className="text-red-500 text-xs mt-1">{errors.duration}</Typography>
                )}
              </div>
            </div>

            {/* Start and End Time (conditional) */}
            {useStartEndTime && (
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Typography className="mb-1 block font-medium text-text-primary">
                    Start Time
                  </Typography>
                  <Input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    noWrapper
                  />
                </div>
                <div>
                  <Typography className="mb-1 block font-medium text-text-primary">
                    End Time
                  </Typography>
                  <Input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    noWrapper
                  />
                </div>
              </div>
            )}

            {showBillableHours ? (
              <div className="mb-4">
                <Typography className="mb-1 block font-medium text-text-primary">
                  Billable hours
                </Typography>
                <div className="flex items-start gap-2">
                  <RiFocus3Line className="text-text-secondary w-5 h-5 flex-shrink-0 mt-2.5" />
                  <div className="flex-1">
                    <Input
                      value={billableDuration}
                      onChange={handleBillableDurationChange}
                      placeholder="e.g. 2h (cannot exceed logged time)"
                      className={`h-10 ${errors.billableDuration ? "border-red-500" : ""}`}
                      noWrapper
                    />
                    {billableHoursPreview ? (
                      <Typography variant="text2" className="text-text-secondary text-xs mt-1">
                        ≈ {billableHoursPreview} billable{" "}
                        <span className="sr-only">hours (Jira-style)</span>
                      </Typography>
                    ) : null}
                    {errors.billableDuration ? (
                      <Typography className="text-red-500 text-xs mt-1">
                        {errors.billableDuration}
                      </Typography>
                    ) : (
                      <Typography
                        variant="text2"
                        className="text-text-secondary text-xs mt-1"
                      ></Typography>
                    )}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Description Field */}
            <div className="mb-6">
              <Typography className="mb-1 block font-medium text-text-primary">
                Description
              </Typography>
              <div className="flex items-start gap-2">
                <RiMenuLine className="text-text-secondary w-5 h-5 flex-shrink-0 mt-3" />
                <div className="flex-1">
                  <Input
                    type="textarea"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Add a description..."
                    rows={4}
                    noWrapper
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            {submitError && (
              <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2">
                <Typography className="text-red-700 text-sm">{submitError}</Typography>
              </div>
            )}
            <div className="flex items-center justify-between pt-4">
              {mode === "create" ? (
                <Checkbox
                  name="logAnother"
                  checked={logAnother}
                  onChange={(e) => setLogAnother(e.target.checked)}
                  label="Log another"
                />
              ) : (
                <span />
              )}
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="danger"
                  size="md"
                  onClick={handleClose}
                  className="btn-reset"
                >
                  Cancel
                </Button>
                <FormSubmitButton
                  loading={submitting}
                  label={mode === "edit" ? "Update time" : "Log time"}
                  loadingText={mode === "edit" ? "Updating…" : "Logging…"}
                  idleIconClass=""
                  size="md"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LogTimeModal;
